<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯通知範本' : '新增通知範本' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="handleSubmit">
          {{ saving ? '儲存中...' : (isEdit ? '更新範本' : '建立範本') }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else class="form-layout">
      <!-- 左側：表單 -->
      <div class="card form-card">
        <div class="card-body">
          <form @submit.prevent="handleSubmit">

            <!-- 基本資訊 -->
            <div class="form-section">
              <h2 class="section-title">基本資訊</h2>

              <div class="form-group">
                <label class="form-label required-label">範本名稱</label>
                <input
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.name }"
                  placeholder="例如：生日祝福 Email"
                  maxlength="100"
                />
                <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
              </div>

              <div class="form-group">
                <label class="form-label required-label">通知管道</label>
                <div class="channel-selector">
                  <label
                    v-for="ch in channelOptions"
                    :key="ch.value"
                    class="channel-option"
                    :class="{ active: form.channel === ch.value, disabled: isEdit }"
                  >
                    <input
                      type="radio"
                      :value="ch.value"
                      v-model="form.channel"
                      :disabled="isEdit"
                    />
                    <i :class="ch.icon"></i>
                    <span>{{ ch.label }}</span>
                  </label>
                </div>
                <small v-if="isEdit" class="form-text">管道建立後無法修改</small>
              </div>

              <div class="form-group">
                <label class="form-label">狀態</label>
                <div class="toggle-row">
                  <label class="toggle-switch">
                    <input
                      v-model="form.status"
                      type="checkbox"
                      true-value="active"
                      false-value="inactive"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                  <span class="toggle-status" :class="form.status === 'active' ? 'on' : 'off'">
                    {{ form.status === 'active' ? '已啟用' : '已停用' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 範本內容 -->
            <div class="form-section">
              <h2 class="section-title">範本內容</h2>

              <!-- Email -->
              <template v-if="form.channel === 'email'">
                <div class="form-group">
                  <label class="form-label required-label">郵件主旨</label>
                  <input
                    ref="subjectFieldRef"
                    v-model="form.subject"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.subject }"
                    :placeholder="placeholderEmailSubject"
                    maxlength="255"
                    @focus="onSubjectFieldFocus"
                  />
                  <span v-if="errors.subject" class="error-text">{{ errors.subject }}</span>
                </div>
                <div class="form-group">
                  <label class="form-label required-label">郵件內容</label>
                  <textarea
                    ref="bodyFieldRef"
                    v-model="form.bodyText"
                    class="form-control"
                    :class="{ 'is-invalid': errors.bodyText }"
                    rows="10"
                    :placeholder="placeholderEmailBody"
                    @focus="onBodyFieldFocus"
                  ></textarea>
                  <span v-if="errors.bodyText" class="error-text">{{ errors.bodyText }}</span>
                </div>
              </template>

              <!-- SMS -->
              <template v-else-if="form.channel === 'sms'">
                <div class="form-group">
                  <label class="form-label required-label">簡訊內容</label>
                  <textarea
                    ref="bodyFieldRef"
                    v-model="form.bodyText"
                    class="form-control"
                    :class="{ 'is-invalid': errors.bodyText }"
                    rows="5"
                    :placeholder="placeholderSms"
                    @focus="onBodyFieldFocus"
                  ></textarea>
                  <div class="sms-counter" :class="{ danger: smsCharCount > 70 }">
                    {{ smsCharCount }} 字（超過 70 字將分段發送）
                  </div>
                  <span v-if="errors.bodyText" class="error-text">{{ errors.bodyText }}</span>
                </div>
              </template>

              <!-- 未選管道提示 -->
              <div v-else class="empty-channel-hint">
                請先選擇通知管道
              </div>
            </div>

            <!-- 支援變數宣告 -->
            <div v-if="form.channel" class="form-section">
              <h2 class="section-title">變數宣告</h2>
              <div class="variables-tag-input">
                <TemplateVariableShortcuts
                  v-model="form.variables"
                  :items="availableVariables"
                  behavior="insert"
                  :title="''"
                  @insert="insertVariableKey"
                >
                  <template #hint>
                    <p class="form-hint">
                      宣告此範本使用的變數（系統將在發送時自動替換）。點選下方按鈕會插入
                      <code v-pre>{{變數}}</code>
                      至<strong>最後聚焦</strong>的主旨或內文欄位游標處（請先點一下輸入框）；若該變數尚未宣告會一併加入上方標籤。
                    </p>
                  </template>
                </TemplateVariableShortcuts>
              </div>
            </div>

          </form>
        </div>
      </div>

      <!-- 右側：管道與情境說明（變數操作請用左側「變數宣告」） -->
      <div class="help-panel">
        <div class="card">
          <div class="card-body">
            <h3 class="help-title"><i class="ri-information-line"></i> 使用說明</h3>
            <p class="help-intro">
              插入 <code v-pre>{{變數}}</code> 請使用左側「變數宣告」的快速按鈕；請先點一下主旨或內文輸入框再點選。
            </p>

            <div class="help-block help-block--compact help-block--first">
              <h4>點數過期提醒（Email／SMS）</h4>
              <p class="help-inline-text">
                在「點數過期設定」選擇的通知範本，發送時會帶入
                <code v-pre>{{memberName}}</code>、<code v-pre>{{totalPoints}}</code>、<code v-pre>{{expiryDate}}</code>、<code v-pre>{{daysUntilExpiry}}</code>、<code v-pre>{{reminderDays}}</code>（與 LINE 預設文案相同語意）。
                與禮物發送的 <code v-pre>{{pointsAmount}}</code> 不同，請勿混用。
              </p>
            </div>

            <div v-if="form.channel === 'email'" class="help-block">
              <h4>Email 說明</h4>
              <ul>
                <li>主旨與內文都支援變數替換</li>
                <li>確保收件人有在系統中設定 Email</li>
                <li>需要 AWS SES 已驗證寄件人</li>
              </ul>
            </div>

            <div v-if="form.channel === 'sms'" class="help-block">
              <h4>SMS 說明</h4>
              <ul>
                <li>建議保持在 70 字以內避免分段</li>
                <li>確保收件人有設定手機號碼</li>
                <li>需要 AWS SNS 權限與預算</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { notificationTemplateService } from '@/api/modules/notificationTemplates.js';
import TemplateVariableShortcuts from '@/components/TemplateVariableShortcuts.vue';
import { useSubjectBodyVariableInsert } from '@/composables/useSubjectBodyVariableInsert.js';

const router = useRouter();
const route = useRoute();

/** 勿寫在模板字面 placeholder 中，否則 {{xxx}} 會被 Vue 當成插值 */
const placeholderEmailSubject =
  '例如：{{memberName}}，您有一份生日禮等您領取';
const placeholderEmailBody =
  '親愛的 {{memberName}} 您好，\n\n感謝您一直以來的支持！\n您的生日禮物已準備好，請至App查看。';
const placeholderSms =
  '{{memberName}}，生日快樂！您的生日禮已發放，詳情請至App查看。';

const isEdit = computed(() => !!route.params.id);
const loading = ref(false);
const saving = ref(false);

const form = reactive({
  name: '',
  channel: '',
  subject: '',
  bodyText: '',
  bodyFlexJson: null,
  variables: [],
  status: 'active'
});

const errors = reactive({});
const flexJsonText = ref('');
const flexJsonError = ref('');

const channelOptions = [
  { value: 'email', label: 'Email', icon: 'ri-mail-line' },
  { value: 'sms', label: 'SMS', icon: 'ri-message-2-line' }
];

const availableVariables = [
  { key: 'memberName', label: '會員姓名' },
  { key: 'memberCode', label: '會員編號' },
  { key: 'pointsAmount', label: '點數數量（註冊禮／升等禮等自動發送）' },
  { key: 'couponCount', label: '優惠券數量' },
  { key: 'tierName', label: '會員等級' },
  // 與 api/schedulers/jobs/checkPointsExpiryReminder.js 注入變數一致（點數過期設定 → 提醒排程）
  { key: 'totalPoints', label: '即將到期點數合計（點數到期提醒）' },
  { key: 'expiryDate', label: '最近到期日 YYYY-MM-DD（點數到期提醒）' },
  { key: 'daysUntilExpiry', label: '距離到期曆天數（點數到期提醒）' },
  { key: 'reminderDays', label: '本輪提醒閾值／到期前天數（點數到期提醒）' }
];

const smsCharCount = computed(() => (form.bodyText || '').length);

/** 模板 ref 須在父層宣告再傳入 composable，否則 ref="subjectFieldRef" 可能無法綁定 */
const subjectFieldRef = ref(null);
const bodyFieldRef = ref(null);

const {
  onSubjectFieldFocus,
  onBodyFieldFocus,
  insertVariableKey
} = useSubjectBodyVariableInsert(form, {
  subjectFieldRef,
  bodyFieldRef
});

function validateFlexJson() {
  flexJsonError.value = '';
  const text = flexJsonText.value.trim();
  if (!text) {
    form.bodyFlexJson = null;
    return;
  }
  try {
    form.bodyFlexJson = JSON.parse(text);
  } catch (e) {
    flexJsonError.value = 'JSON 格式錯誤：' + e.message;
    form.bodyFlexJson = null;
  }
}

function validate() {
  Object.keys(errors).forEach(k => delete errors[k]);
  if (!form.name) errors.name = '請填寫範本名稱';
  if (!form.channel) errors.channel = '請選擇通知管道';
  if (form.channel === 'email') {
    if (!form.subject) errors.subject = '請填寫郵件主旨';
    if (!form.bodyText) errors.bodyText = '請填寫郵件內容';
  }
  if (form.channel === 'sms' && !form.bodyText) {
    errors.bodyText = '請填寫簡訊內容';
  }
  return Object.keys(errors).length === 0;
}

async function handleSubmit() {
  if (!validate()) return;
  saving.value = true;
  try {
    const payload = {
      name: form.name,
      channel: form.channel,
      subject: form.subject || undefined,
      bodyText: form.bodyText || undefined,
      bodyFlexJson: form.bodyFlexJson || undefined,
      variables: form.variables,
      status: form.status
    };
    if (isEdit.value) {
      await notificationTemplateService.update(route.params.id, payload);
    } else {
      await notificationTemplateService.create(payload);
    }
    router.push({ name: 'NotificationTemplates' });
  } catch (err) {
    console.error('儲存失敗:', err);
    alert('儲存失敗：' + (err?.response?.data?.message || err.message || '未知錯誤'));
  } finally {
    saving.value = false;
  }
}

function goBack() {
  router.push({ name: 'NotificationTemplates' });
}

async function loadTemplate() {
  loading.value = true;
  try {
    const res = await notificationTemplateService.getById(route.params.id);
    const data = res.data;
    Object.assign(form, {
      name: data.name || '',
      channel: data.channel || '',
      subject: data.subject || '',
      bodyText: data.bodyText || '',
      bodyFlexJson: data.bodyFlexJson || null,
      variables: data.variables || [],
      status: data.status || 'active'
    });
    if (data.bodyFlexJson) {
      flexJsonText.value = JSON.stringify(data.bodyFlexJson, null, 2);
    }
  } catch (err) {
    console.error('載入範本失敗:', err);
    alert('載入範本失敗');
    goBack();
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (isEdit.value) {
    loadTemplate();
  }
});
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.form-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 20px;
  align-items: start;
}

@media (max-width: 1024px) {
  .form-layout {
    grid-template-columns: 1fr;
  }
}

.form-card {
  flex: 1;
}

/* 與 line-msg-templates/Editor 等頁一致：此頁未使用全域 .card，需自行定義 */
.form-layout .card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid #e5e7eb;
}

.form-layout .card .card-body {
  padding: 20px;
}

.form-section {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f3f4f6;
}

.form-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.required-label::after {
  content: ' *';
  color: #ef4444;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control.is-invalid {
  border-color: #ef4444;
}

.error-text {
  display: block;
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
}

.form-hint {
  font-size: 13px;
  color: #6b7280;
  margin: -8px 0 12px;
}

.form-text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  display: block;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .toggle-slider {
  background: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.toggle-status.on {
  color: #4caf50;
}

.toggle-status.off {
  color: #9ca3af;
}

/* 管道選擇器 */
.channel-selector {
  display: flex;
  gap: 12px;
}

.channel-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.channel-option input[type="radio"] {
  display: none;
}

.channel-option i {
  font-size: 24px;
  color: #9ca3af;
}

.channel-option span {
  font-size: 13px;
  color: #6b7280;
}

.channel-option.active {
  border-color: #3b82f6;
  background: #eff6ff;
}

.channel-option.active i,
.channel-option.active span {
  color: #3b82f6;
}

.channel-option.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 代碼編輯器 */
.code-editor {
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
}

.editor-help {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
}

.editor-help a {
  color: #3b82f6;
  text-decoration: none;
}

/* SMS 字數統計 */
.sms-counter {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  text-align: right;
}

.sms-counter.danger {
  color: #f59e0b;
}

/* 變數宣告區外框 */
.variables-tag-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 12px;
}

/* 未選管道提示 */
.empty-channel-hint {
  text-align: center;
  padding: 40px;
  color: #9ca3af;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
}

/* 右側說明面板 */
.help-panel {
  position: sticky;
  top: 88px;
}

.help-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
}

.help-intro {
  margin: 0 0 14px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.6;
}

.help-intro code {
  background: #f3f4f6;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 11px;
  color: #374151;
}

.help-block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
}

.help-block h4 {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 8px;
  color: #374151;
}

.help-block ul {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.8;
}

.help-block a {
  color: #3b82f6;
  text-decoration: none;
}

.help-block--compact {
  margin-top: 12px;
  padding-top: 12px;
}

.help-block--first {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.help-inline-text {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.65;
}

.help-inline-text code {
  background: #f3f4f6;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 11px;
}
</style>
